import React, { useEffect, useMemo, useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import SellRoundedIcon from "@mui/icons-material/SellRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PauseCircleRoundedIcon from "@mui/icons-material/PauseCircleRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";

import { useNavigate } from "react-router-dom";

// ✅ RTDB
import { rtdb } from "../../services/firebase/rtdb";
import { ref, onValue, off } from "firebase/database";

// ✅ seu auth context (opcional aqui, só pro nome no header)
import { useAuthContext } from "../../contexts/AuthContext";

/**
 * Helpers
 */
function toDateMs(ms) {
  if (!ms) return null;
  const n = Number(ms);
  if (!n || Number.isNaN(n)) return null;
  const d = new Date(n);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatBRL(n) {
  const v = Number(n || 0);
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatShortDate(d) {
  if (!d) return "-";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function formatDateTime(d) {
  if (!d) return "-";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function computeStatus(event) {
  const now = new Date();

  // seu shape:
  const start = toDateMs(event.startAtMs);
  const end = toDateMs(event.endAtMs);
  const manual = String(event.status || "").toLowerCase();

  if (manual === "cancelled" || manual === "canceled") return "cancelled";
  if (manual === "paused") return "paused";
  if (manual === "draft") return "draft";

  if (!start && !end) return manual || "draft";

  if (start && now < start) return "scheduled";
  if (start && (!end || now <= end)) return "live";
  if (end && now > end) return "ended";

  return manual || "scheduled";
}

function statusChipProps(status, theme) {
  const map = {
    live: {
      label: "Ao vivo",
      icon: <CheckCircleRoundedIcon />,
      color: theme.palette.success.main,
    },
    scheduled: {
      label: "Agendado",
      icon: <AccessTimeRoundedIcon />,
      color: theme.palette.info.main,
    },
    ended: {
      label: "Encerrado",
      icon: <PauseCircleRoundedIcon />,
      color: theme.palette.text.secondary,
    },
    draft: {
      label: "Rascunho",
      icon: <EditRoundedIcon />,
      color: theme.palette.warning.main,
    },
    paused: {
      label: "Pausado",
      icon: <PauseCircleRoundedIcon />,
      color: theme.palette.warning.dark,
    },
    cancelled: {
      label: "Cancelado",
      icon: <CancelRoundedIcon />,
      color: theme.palette.error.main,
    },
  };
  const item = map[status] || map.draft;

  return {
    label: item.label,
    icon: item.icon,
    sx: {
      height: 28,
      borderRadius: 1,
      color: alpha(item.color, 0.95),
      bgcolor: alpha(item.color, 0.12),
      border: `1px solid ${alpha(item.color, 0.22)}`,
      "& .MuiChip-icon": { color: alpha(item.color, 0.9) },
      fontWeight: 900,
    },
  };
}

function selectSx(theme) {
  return {
    borderRadius: 1,
    bgcolor: alpha("#fff", 0.01),
    color: "rgba(255,255,255,0.90)",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: alpha("#fff", 0.14) },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha("#fff", 0.22),
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: alpha(theme.palette.primary.main, 0.55),
    },
    "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.65)" },
  };
}

function miniChipSx() {
  return {
    height: 28,
    borderRadius: 1,
    bgcolor: alpha("#fff", 0.05),
    border: `1px solid ${alpha("#fff", 0.12)}`,
    color: "rgba(255,255,255,0.75)",
    fontWeight: 900,
    "& .MuiChip-icon": { color: "rgba(255,255,255,0.65)" },
  };
}

function RowKpi({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 1,
          display: "grid",
          placeItems: "center",
          bgcolor: alpha("#fff", 0.05),
          border: `1px solid ${alpha("#fff", 0.1)}`,
          color: "rgba(255,255,255,0.70)",
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ color: "rgba(255,255,255,0.60)", fontSize: 12 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Typography
        sx={{ color: "rgba(255,255,255,0.90)", fontWeight: 900, fontSize: 12 }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function StatPill({ icon, label, value }) {
  return (
    <Paper
      sx={{
        flex: 1,
        borderRadius: 1,
        border: `1px solid ${alpha("#fff", 0.1)}`,
        bgcolor: alpha("#fff", 0.01),
        boxShadow: `0 10px 40px ${alpha("#000", 0.35)}`,
        backdropFilter: "blur(18px)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1.6 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha("#fff", 0.06),
              border: `1px solid ${alpha("#fff", 0.1)}`,
            }}
          >
            {icon}
          </Box>
          <Box>
            <Typography sx={{ color: "rgba(255,255,255,0.60)", fontSize: 12 }}>
              {label}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.92)",
                fontWeight: 1000,
                letterSpacing: -0.2,
                fontSize: 16,
              }}
            >
              {value}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}

function EventCard({ e, theme, glassCardSx, onEdit, onManage, onPublic }) {
  const chip = statusChipProps(e.statusComputed, theme);

  return (
    <Paper sx={{ ...glassCardSx }}>
      {/* ── Mobile: layout horizontal compacto ── */}
      <Box sx={{ display: { xs: "block", sm: "none" } }}>
        <Stack direction="row" spacing={0} sx={{ overflow: "hidden", borderRadius: "inherit" }}>
          {/* Thumbnail lateral */}
          <Box
            sx={{
              width: 90,
              flexShrink: 0,
              minHeight: 90,
              backgroundImage: e.bannerUrl ? `url(${e.bannerUrl})` : "none",
              backgroundSize: "cover",
              backgroundPosition: "center",
              bgcolor: alpha("#fff", 0.06),
              display: "grid",
              placeItems: "center",
              color: "rgba(255,255,255,0.4)",
            }}
          >
            {!e.bannerUrl && <EventRoundedIcon />}
          </Box>

          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0, p: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.5}>
              <Typography
                sx={{ fontWeight: 900, fontSize: 14, color: "rgba(255,255,255,0.92)", lineHeight: 1.2, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {e.title}
              </Typography>
              <Chip
                {...chip}
                sx={{
                  ...chip.sx,
                  height: 22,
                  fontSize: "0.65rem",
                  flexShrink: 0,
                  "& .MuiChip-icon": { fontSize: 12 },
                }}
              />
            </Stack>

            <Stack direction="row" spacing={0.6} sx={{ mt: 0.8, flexWrap: "wrap", gap: 0.5 }}>
              <Chip
                icon={<PlaceRoundedIcon sx={{ fontSize: "12px !important" }} />}
                label={e.city || "—"}
                size="small"
                sx={{ ...miniChipSx(), height: 22, fontSize: "0.65rem" }}
              />
              <Chip
                icon={<CalendarMonthRoundedIcon sx={{ fontSize: "12px !important" }} />}
                label={formatShortDate(e.start)}
                size="small"
                sx={{ ...miniChipSx(), height: 22, fontSize: "0.65rem" }}
              />
              <Chip
                icon={<AttachMoneyRoundedIcon sx={{ fontSize: "12px !important" }} />}
                label={e.priceRangeLabel}
                size="small"
                sx={{ ...miniChipSx(), height: 22, fontSize: "0.65rem" }}
              />
            </Stack>
          </Box>
        </Stack>

        {/* Botões na base */}
        <Stack direction="row" spacing={1} sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
          <Button
            onClick={onManage}
            fullWidth
            variant="contained"
            size="small"
            sx={{ borderRadius: 1, textTransform: "none", fontWeight: 900, fontSize: 13 }}
            startIcon={<VisibilityRoundedIcon sx={{ fontSize: "16px !important" }} />}
          >
            Gerenciar
          </Button>
          <IconButton
            onClick={onEdit}
            size="small"
            sx={{ borderRadius: 1, border: `1px solid ${alpha("#fff", 0.12)}`, bgcolor: alpha("#fff", 0.01) }}
          >
            <EditRoundedIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={onPublic}
            size="small"
            sx={{ borderRadius: 1, border: `1px solid ${alpha("#fff", 0.12)}`, bgcolor: alpha("#fff", 0.01) }}
          >
            <OpenInNewRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* ── Desktop: layout original ── */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <Box
          sx={{
            position: "relative",
            height: 150,
            bgcolor: alpha("#fff", 0.06),
            borderBottom: `1px solid ${alpha("#fff", 0.1)}`,
            overflow: "hidden",
          }}
        >
          {e.bannerUrl ? (
            <Box
              component="img"
              src={e.bannerUrl}
              alt={e.title}
              sx={{ width: "100%", height: "100%", objectFit: "cover", filter: "saturate(1.05) contrast(1.02)", transform: "scale(1.02)" }}
            />
          ) : (
            <Box sx={{ width: "100%", height: "100%", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.55)" }}>
              <EventRoundedIcon fontSize="large" />
            </Box>
          )}
          <Box sx={{ position: "absolute", top: 12, left: 12 }}>
            <Chip {...chip} />
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Stack spacing={1.1}>
            <Typography sx={{ fontWeight: 1000, color: "rgba(255,255,255,0.92)", letterSpacing: -0.25, lineHeight: 1.15, fontSize: 16 }} title={e.title}>
              {e.title}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              <Chip icon={<PlaceRoundedIcon />} label={e.city || "—"} sx={miniChipSx()} />
              <Chip icon={<CalendarMonthRoundedIcon />} label={formatShortDate(e.start)} sx={miniChipSx()} />
              <Chip label={e.category || "—"} sx={miniChipSx()} />
            </Stack>

            <Divider sx={{ borderColor: alpha("#fff", 0.1) }} />

            <Stack spacing={0.7}>
              <RowKpi icon={<AccessTimeRoundedIcon />} label="Início" value={formatDateTime(e.start)} />
              <RowKpi icon={<PauseCircleRoundedIcon />} label="Fim" value={formatDateTime(e.end)} />
              <RowKpi icon={<SellRoundedIcon />} label="Tickets cadastrados" value={e.ticketsCount} />
              <RowKpi icon={<AttachMoneyRoundedIcon />} label="Faixa de preço" value={e.priceRangeLabel} />
            </Stack>

            <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
              <Button onClick={onManage} fullWidth variant="contained" sx={{ borderRadius: 1, textTransform: "none", fontWeight: 900 }} startIcon={<VisibilityRoundedIcon />}>
                Gerenciar
              </Button>
              <Tooltip title="Editar">
                <IconButton onClick={onEdit} sx={{ borderRadius: 1, border: `1px solid ${alpha("#fff", 0.12)}`, bgcolor: alpha("#fff", 0.01) }}>
                  <EditRoundedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Abrir público">
                <IconButton onClick={onPublic} sx={{ borderRadius: 1, border: `1px solid ${alpha("#fff", 0.12)}`, bgcolor: alpha("#fff", 0.01) }}>
                  <OpenInNewRoundedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
}

export default function Events() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { profile, user } = useAuthContext();
  const organizerId = user?.uid || profile?.uid || null;

  const glassCardSx = useMemo(
    () => ({
      borderRadius: 1,
      border: `1px solid ${alpha("#fff", 0.1)}`,
      background: `linear-gradient(180deg, ${alpha("#fff", 0.08)} 0%, ${alpha("#fff", 0.04)} 100%)`,
      boxShadow: `0 16px 60px ${alpha("#000", 0.4)}`,
      backdropFilter: "blur(18px)",
      overflow: "hidden",
    }),
    [],
  );

  const [viewMode, setViewMode] = useState(isMdUp ? "grid" : "table");
  const [loading, setLoading] = useState(true);

  const [eventsRaw, setEventsRaw] = useState([]);

  // filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("startAsc");

  // ✅ FETCH TODOS OS EVENTOS
  useEffect(() => {
    setLoading(true);

    const eventsRef = ref(rtdb, "events");

    const handler = (snap) => {
      const val = snap.val();
      const list = val
        ? Object.entries(val).map(([id, data]) => ({ id, ...(data || {}) }))
        : [];
      setEventsRaw(list);
      setLoading(false);
    };

    onValue(eventsRef, handler, (err) => {
      console.error("Erro ao carregar /events:", err);
      setEventsRaw([]);
      setLoading(false);
    });

    return () => {
      off(eventsRef, "value", handler);
    };
  }, []);

  const normalized = useMemo(() => {
    return (eventsRaw || [])
      .filter((e) => !organizerId || e.organizerId === organizerId)
      .map((e) => {
      const start = toDateMs(e.timing?.startAtMs ?? e.startAtMs);
      const end = toDateMs(e.timing?.endAtMs ?? e.endAtMs);
      const created = toDateMs(e.createdAtMs);

      const statusComputed = computeStatus({
        ...e,
        startAtMs: e.timing?.startAtMs ?? e.startAtMs,
        endAtMs: e.timing?.endAtMs ?? e.endAtMs,
      });

      const city = e.location?.city || "—";
      const category = e.category || "—";

      const ticketsCount = Number(e.stats?.ticketTypesCount || 0);
      const minPrice = Number(e.stats?.minTicketPrice || 0);
      const maxPrice = Number(e.stats?.maxTicketPrice || 0);

      const priceRangeLabel =
        minPrice && maxPrice
          ? minPrice === maxPrice
            ? formatBRL(minPrice)
            : `${formatBRL(minPrice)} – ${formatBRL(maxPrice)}`
          : "—";

      return {
        ...e,
        start,
        end,
        created,
        city,
        category,
        statusComputed,
        ticketsCount,
        priceRangeLabel,
        slug: e.seo?.slug || e.slug || null,
      };
    });
  }, [eventsRaw, organizerId]);

  const cities = useMemo(() => {
    const set = new Set();
    normalized.forEach((e) => e.city && e.city !== "—" && set.add(e.city));
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [normalized]);

  const categories = useMemo(() => {
    const set = new Set();
    normalized.forEach(
      (e) => e.category && e.category !== "—" && set.add(e.category),
    );
    return ["all", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [normalized]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();

    let list = normalized.filter((e) => {
      const okSearch =
        !s ||
        String(e.title || "")
          .toLowerCase()
          .includes(s) ||
        String(e.city || "")
          .toLowerCase()
          .includes(s) ||
        String(e.id).toLowerCase().includes(s);

      const okStatus =
        statusFilter === "all" || e.statusComputed === statusFilter;
      const okCity = cityFilter === "all" || e.city === cityFilter;
      const okCat = categoryFilter === "all" || e.category === categoryFilter;

      return okSearch && okStatus && okCity && okCat;
    });

    list = [...list].sort((a, b) => {
      const aStart = a.start ? a.start.getTime() : 0;
      const bStart = b.start ? b.start.getTime() : 0;
      const aCreated = a.created ? a.created.getTime() : 0;
      const bCreated = b.created ? b.created.getTime() : 0;

      if (sortBy === "startAsc") return aStart - bStart;
      if (sortBy === "startDesc") return bStart - aStart;
      if (sortBy === "createdDesc") return bCreated - aCreated;

      return 0;
    });

    return list;
  }, [normalized, search, statusFilter, cityFilter, categoryFilter, sortBy]);

  const stats = useMemo(() => {
    const total = normalized.length;
    const live = normalized.filter((e) => e.statusComputed === "live").length;
    const scheduled = normalized.filter(
      (e) => e.statusComputed === "scheduled",
    ).length;
    const ended = normalized.filter((e) => e.statusComputed === "ended").length;
    const draft = normalized.filter((e) => e.statusComputed === "draft").length;

    const tickets = normalized.reduce(
      (acc, e) => acc + (e.ticketsCount || 0),
      0,
    );

    return { total, live, scheduled, ended, draft, tickets };
  }, [normalized]);

  const statusChips = useMemo(
    () => [
      { key: "all", label: "Todos", count: stats.total },
      { key: "live", label: "Ao vivo", count: stats.live },
      { key: "scheduled", label: "Agendados", count: stats.scheduled },
      { key: "ended", label: "Encerrados", count: stats.ended },
      { key: "draft", label: "Rascunhos", count: stats.draft },
    ],
    [stats],
  );

  const goCreate = () => navigate("/dashboard/eventos/novo");
  const goEdit = (id) => navigate(`/dashboard/eventos/${id}`);
  const goManage = (id) => navigate(`/dashboard/eventos/${id}`);
  const goPublic = (slugOrId) =>
    window.open(`/eventos/${slugOrId}`, "_blank", "noopener,noreferrer");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Paper sx={{ ...glassCardSx, mb: 3 }}>
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Stack spacing={0.6}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 1,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.14),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                    }}
                  >
                    <EventRoundedIcon />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 1000,
                        letterSpacing: -0.3,
                        color: "rgba(255,255,255,0.92)",
                        fontSize: { xs: 22, md: 26 },
                        lineHeight: 1.1,
                      }}
                    >
                      Eventos
                    </Typography>
                    <Typography
                      sx={{ color: "rgba(255,255,255,0.62)", fontSize: 13 }}
                    >
                      {profile?.displayName
                        ? `Organizador: ${profile.displayName}`
                        : "Listagem geral (modo dev — sem organizerId)."}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1.2} alignItems="center">
                <Tooltip title="Atualizar">
                  <IconButton
                    onClick={() => {
                      setLoading(true);
                      setTimeout(() => setLoading(false), 250);
                    }}
                    sx={{
                      borderRadius: 1,
                      border: `1px solid ${alpha("#fff", 0.12)}`,
                      bgcolor: alpha("#fff", 0.01),
                    }}
                  >
                    <RefreshRoundedIcon />
                  </IconButton>
                </Tooltip>

                <Button
                  onClick={goCreate}
                  startIcon={<AddRoundedIcon />}
                  variant="contained"
                  sx={{
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: 900,
                    px: 2,
                  }}
                >
                  Criar evento
                </Button>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2, borderColor: alpha("#fff", 0.1) }} />

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              sx={{ mb: 1 }}
            >
              <StatPill
                icon={<CalendarMonthRoundedIcon />}
                label="Total"
                value={stats.total}
              />
              <StatPill
                icon={<CheckCircleRoundedIcon />}
                label="Ao vivo"
                value={stats.live}
              />
              <StatPill
                icon={<AccessTimeRoundedIcon />}
                label="Agendados"
                value={stats.scheduled}
              />
              <StatPill
                icon={<PauseCircleRoundedIcon />}
                label="Encerrados"
                value={stats.ended}
              />
              <StatPill
                icon={<SellRoundedIcon />}
                label="Tipos de ticket"
                value={stats.tickets}
              />
              <StatPill
                icon={<AttachMoneyRoundedIcon />}
                label="Preço (exemplo)"
                value="—"
              />
            </Stack>
          </Box>
        </Paper>

        {/* Filters */}
        <Paper sx={{ ...glassCardSx, mb: 3 }}>
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha("#fff", 0.06),
                    border: `1px solid ${alpha("#fff", 0.1)}`,
                  }}
                >
                  <TuneRoundedIcon fontSize="small" />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 1000,
                    color: "rgba(255,255,255,0.86)",
                    letterSpacing: -0.2,
                  }}
                >
                  Filtros
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ display: { xs: "none", sm: "flex" } }}>
                <Tooltip title="Grid">
                  <IconButton
                    onClick={() => setViewMode("grid")}
                    sx={{
                      borderRadius: 1,
                      border: `1px solid ${alpha("#fff", 0.12)}`,
                      bgcolor: viewMode === "grid" ? alpha(theme.palette.primary.main, 0.16) : alpha("#fff", 0.04),
                    }}
                  >
                    <GridViewRoundedIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Tabela">
                  <IconButton
                    onClick={() => setViewMode("table")}
                    sx={{
                      borderRadius: 1,
                      border: `1px solid ${alpha("#fff", 0.12)}`,
                      bgcolor: viewMode === "table" ? alpha(theme.palette.primary.main, 0.16) : alpha("#fff", 0.04),
                    }}
                  >
                    <TableRowsRoundedIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.2}
              sx={{ mt: 2 }}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, cidade ou ID…"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                    bgcolor: alpha("#fff", 0.01),
                    color: "rgba(255,255,255,0.90)",
                    "& fieldset": { borderColor: alpha("#fff", 0.14) },
                    "&:hover fieldset": { borderColor: alpha("#fff", 0.22) },
                    "&.Mui-focused fieldset": {
                      borderColor: alpha(theme.palette.primary.main, 0.55),
                    },
                  },
                  "& input::placeholder": {
                    color: "rgba(255,255,255,0.45)",
                    opacity: 1,
                  },
                }}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <Select
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    displayEmpty
                    sx={selectSx(theme)}
                  >
                    <MenuItem value="all">Todas as cidades</MenuItem>
                    {cities
                      .filter((c) => c !== "all")
                      .map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    displayEmpty
                    sx={selectSx(theme)}
                  >
                    <MenuItem value="all">Todas as categorias</MenuItem>
                    {categories
                      .filter((c) => c !== "all")
                      .map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 190 }}>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={selectSx(theme)}
                  >
                    <MenuItem value="startAsc">
                      Data (próximos primeiro)
                    </MenuItem>
                    <MenuItem value="startDesc">
                      Data (recentes primeiro)
                    </MenuItem>
                    <MenuItem value="createdDesc">
                      Criados recentemente
                    </MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap" }}>
              {statusChips.map((s) => (
                <Chip
                  key={s.key}
                  label={`${s.label} • ${s.count}`}
                  onClick={() => setStatusFilter(s.key)}
                  sx={{
                    height: 34,
                    borderRadius: 1,
                    fontWeight: 1000,
                    cursor: "pointer",
                    color:
                      statusFilter === s.key
                        ? "rgba(255,255,255,0.92)"
                        : "rgba(255,255,255,0.70)",
                    bgcolor:
                      statusFilter === s.key
                        ? alpha(theme.palette.primary.main, 0.18)
                        : alpha("#fff", 0.05),
                    border: `1px solid ${statusFilter === s.key ? alpha(theme.palette.primary.main, 0.3) : alpha("#fff", 0.12)}`,
                  }}
                />
              ))}
            </Stack>
          </Box>
        </Paper>

        {/* Content */}
        {loading ? (
          <Paper sx={{ ...glassCardSx }}>
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack spacing={2}>
                <Skeleton
                  variant="rounded"
                  height={18}
                  sx={{ bgcolor: alpha("#fff", 0.08), width: 240 }}
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                    gap: 2,
                  }}
                >
                  {[...Array(3)].map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      height={260}
                      sx={{ bgcolor: alpha("#fff", 0.07) }}
                    />
                  ))}
                </Box>
              </Stack>
            </Box>
          </Paper>
        ) : filtered.length === 0 ? (
          <Paper sx={{ ...glassCardSx }}>
            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Stack spacing={1.2} alignItems="center" textAlign="center">
                <Box
                  sx={{
                    width: 68,
                    height: 68,
                    borderRadius: 1,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.14),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                  }}
                >
                  <EventRoundedIcon fontSize="large" />
                </Box>

                <Typography
                  sx={{
                    fontWeight: 1000,
                    fontSize: { xs: 20, md: 24 },
                    color: "rgba(255,255,255,0.92)",
                    letterSpacing: -0.3,
                  }}
                >
                  Nenhum evento encontrado
                </Typography>

                <Typography
                  sx={{ color: "rgba(255,255,255,0.62)", maxWidth: 560 }}
                >
                  Se você tem evento no RTDB, ele deve estar em <b>/events</b>.
                  (Esse modo lista todos, sem organizerId.)
                </Typography>

                <Button
                  onClick={goCreate}
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  sx={{
                    borderRadius: 1,
                    textTransform: "none",
                    fontWeight: 900,
                  }}
                >
                  Criar evento
                </Button>
              </Stack>
            </Box>
          </Paper>
        ) : viewMode === "grid" || !isMdUp ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
                xl: "repeat(4, 1fr)",
              },
              gap: { xs: 1.5, sm: 2 },
            }}
          >
            {filtered.map((e) => (
              <EventCard
                key={e.id}
                e={e}
                theme={theme}
                glassCardSx={glassCardSx}
                onEdit={() => goEdit(e.id)}
                onManage={() => goManage(e.id)}
                onPublic={() => goPublic(e.slug || e.id)}
              />
            ))}
          </Box>
        ) : (
          <Paper sx={{ ...glassCardSx }}>
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              {/* Tabela simplificada: mesma UI da grid já resolve por agora */}
              <Stack spacing={1}>
                {filtered.map((e) => {
                  const chip = statusChipProps(e.statusComputed, theme);
                  return (
                    <Paper
                      key={e.id}
                      sx={{
                        borderRadius: 1,
                        border: `1px solid ${alpha("#fff", 0.1)}`,
                        bgcolor: alpha("#fff", 0.01),
                      }}
                    >
                      <Box sx={{ p: 1.6 }}>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.5}
                          alignItems={{ xs: "stretch", md: "center" }}
                        >
                          <Stack
                            direction="row"
                            spacing={1.2}
                            alignItems="center"
                          >
                            <Avatar
                              variant="rounded"
                              src={e.bannerUrl || undefined}
                              sx={{
                                width: 52,
                                height: 52,
                                borderRadius: 1,
                                bgcolor: alpha("#fff", 0.06),
                                border: `1px solid ${alpha("#fff", 0.1)}`,
                              }}
                            >
                              <EventRoundedIcon />
                            </Avatar>

                            <Box>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ flexWrap: "wrap" }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 1000,
                                    color: "rgba(255,255,255,0.92)",
                                  }}
                                >
                                  {e.title}
                                </Typography>
                                <Chip {...chip} />
                              </Stack>

                              <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mt: 0.7, flexWrap: "wrap" }}
                              >
                                <Chip
                                  icon={<PlaceRoundedIcon />}
                                  label={e.city}
                                  sx={miniChipSx()}
                                />
                                <Chip
                                  icon={<CalendarMonthRoundedIcon />}
                                  label={formatShortDate(e.start)}
                                  sx={miniChipSx()}
                                />
                                <Chip
                                  icon={<SellRoundedIcon />}
                                  label={`Tickets: ${e.ticketsCount}`}
                                  sx={miniChipSx()}
                                />
                                <Chip
                                  icon={<AttachMoneyRoundedIcon />}
                                  label={e.priceRangeLabel}
                                  sx={miniChipSx()}
                                />
                              </Stack>
                            </Box>
                          </Stack>

                          <Box sx={{ flex: 1 }} />

                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Button
                              onClick={() => goManage(e.id)}
                              variant="contained"
                              startIcon={<VisibilityRoundedIcon />}
                              sx={{
                                borderRadius: 1,
                                textTransform: "none",
                                fontWeight: 900,
                              }}
                            >
                              Gerenciar
                            </Button>

                            <Tooltip title="Editar">
                              <IconButton
                                onClick={() => goEdit(e.id)}
                                sx={{
                                  borderRadius: 1,
                                  border: `1px solid ${alpha("#fff", 0.12)}`,
                                  bgcolor: alpha("#fff", 0.01),
                                }}
                              >
                                <EditRoundedIcon />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Abrir público">
                              <IconButton
                                onClick={() => goPublic(e.slug || e.id)}
                                sx={{
                                  borderRadius: 1,
                                  border: `1px solid ${alpha("#fff", 0.12)}`,
                                  bgcolor: alpha("#fff", 0.01),
                                }}
                              >
                                <OpenInNewRoundedIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Paper>
        )}

        <Box sx={{ height: 28 }} />
      </Container>
    </Box>
  );
}
